/**
 * Forgiving product-name search: the owner types fast on a phone, in Russian
 * or Uzbek, in Cyrillic or Latin, sometimes on the wrong keyboard layout and
 * with typos. Everything is folded to one Latin «sound key» before comparing:
 *
 *   «Скумбрия» / «skumbriya» / «scumbria» / «crev,hbz» (EN layout) → skumbriya-ish
 *
 * A query matches when EVERY query word matches SOME word of the name (any
 * order): as a prefix, as a substring (2+ letters), as the word initials, or —
 * from 3 letters on — within a small edit distance (a missed, extra, wrong or
 * swapped letter). Results carry a score so the best matches come first, and
 * an `exact` flag so the page can say «showing similar» when nothing matched
 * literally. Pure, no React — testable in node.
 */

// Russian + Uzbek Cyrillic → Latin, phonetic enough that Latin typing lands on
// the same key (х and Uzbek x both → h; щ → sh; ъ/ь vanish).
// prettier-ignore
const CYR_TO_LAT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'j', з: 'z', и: 'i',
  й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sh', ъ: '', ы: 'i', ь: '',
  э: 'e', ю: 'yu', я: 'ya', ў: 'o', қ: 'k', ғ: 'g', ҳ: 'h', і: 'i', ї: 'i', є: 'e',
};

// Physical key positions: what a Russian word looks like when typed with the
// English layout on, and back.
const EN_KEYS = "`qwertyuiop[]asdfghjkl;'zxcvbnm,.";
const RU_KEYS = 'ёйцукенгшщзхъфывапролджэячсмитьбю';
const EN_TO_RU = new Map([...EN_KEYS].map((c, i) => [c, RU_KEYS[i]!]));
const RU_TO_EN = new Map([...RU_KEYS].map((c, i) => [c, EN_KEYS[i]!]));

/** Lowercased word list: letters and digits only, «с/м» → «с», «м»,
 * «300гр» → «300», «гр». */
function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/['`ʻʼ‘’]/g, '') // Uzbek o' / g' apostrophes belong to the letter
    .replace(/(\p{L})(?=\p{N})|(\p{N})(?=\p{L})/gu, '$1$2 ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w !== '');
}

/** One word → its Latin sound key. */
export function soundKey(word: string): string {
  let lat = '';
  for (const ch of word.toLowerCase()) lat += CYR_TO_LAT[ch] ?? ch;
  return lat
    .replace(/kh/g, 'h')
    .replace(/zh/g, 'j')
    .replace(/x/g, 'h')
    .replace(/q/g, 'k')
    .replace(/w/g, 'v')
    .replace(/c(?!h)/g, 'k')
    .replace(/i?ya|ia/g, 'ya') // «скумбрия» → skumbriya, typed «skumbria»
    .replace(/i?yu|iu/g, 'yu')
    .replace(/(\p{L})\1+/gu, '$1'); // «масса» / «masa»; digits stay («300»)
}

/** Optimal-string-alignment distance (Levenshtein + adjacent swap), capped. */
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev2: number[] = [];
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let d = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d = Math.min(d, prev2[j - 2]! + 1);
      }
      cur.push(d);
      rowMin = Math.min(rowMin, d);
    }
    if (rowMin > max) return max + 1;
    prev2 = prev;
    prev = cur;
  }
  return prev[b.length]!;
}

/** Typos tolerated in a query word of this many letters. */
function allowedTypos(length: number): number {
  if (length < 3) return 0;
  if (length < 8) return 1;
  return 2;
}

/**
 * How well one query word matches one name word, 0 = not at all. `exact`
 * marks literal hits (prefix / substring), the rest are «similar».
 */
function matchWord(q: string, w: string): { score: number; exact: boolean } {
  if (w === q) return { score: 1, exact: true };
  if (w.startsWith(q)) return { score: 0.9, exact: true };
  if (q.length >= 2 && w.includes(q)) return { score: 0.7, exact: true };
  const max = allowedTypos(q.length);
  if (max === 0) return { score: 0, exact: false };
  // A typo anywhere in a prefix counts too: «скубр» → «скумбрия».
  let best = max + 1;
  for (let len = q.length - max; len <= q.length + max; len++) {
    if (len < 1 || len > w.length) continue;
    best = Math.min(best, editDistance(q, w.slice(0, len), max));
  }
  if (best > max) return { score: 0, exact: false };
  return { score: 0.6 * (1 - best / (q.length + 1)), exact: false };
}

/** A name prepared once per row: its words' sound keys. */
export interface SearchIndexEntry {
  keys: string[];
  /** All keys glued — «скумбрия300» still finds «скумбрия 300». */
  joined: string;
  initials: string;
}

export function indexName(name: string): SearchIndexEntry {
  const keys = words(name)
    .map(soundKey)
    .filter((k) => k !== '');
  return { keys, joined: keys.join(''), initials: keys.map((k) => k[0]).join('') };
}

/** The ways a query may have been meant: as typed, or on the other layout. */
function queryVariants(query: string): string[][] {
  const lower = query.toLowerCase();
  const variants = [lower];
  const swap = (map: Map<string, string>) => [...lower].map((c) => map.get(c) ?? c).join('');
  // Two letters on the other layout are a coin toss — guess from three on.
  if (lower.replace(/\s/g, '').length >= 3) {
    if (/[a-z]/.test(lower)) variants.push(swap(EN_TO_RU));
    if (/[а-яё]/.test(lower)) variants.push(swap(RU_TO_EN));
  }
  return variants
    .map((v) =>
      words(v)
        .map(soundKey)
        .filter((k) => k !== ''),
    )
    .filter((v) => v.length > 0);
}

export interface SearchMatch {
  score: number;
  exact: boolean;
}

/** Score one indexed name against pre-split query variants; null = no match. */
function matchEntry(entry: SearchIndexEntry, variants: string[][]): SearchMatch | null {
  let best: SearchMatch | null = null;
  variants.forEach((qWords, variantIndex) => {
    // A wrong-layout reading is a guess — rank it under a literal one.
    const penalty = variantIndex === 0 ? 1 : 0.8;
    let total = 0;
    let exact = true;
    for (const q of qWords) {
      let wordBest = { score: 0, exact: false };
      for (const w of entry.keys) {
        const m = matchWord(q, w);
        if (m.score > wordBest.score) wordBest = m;
      }
      if (wordBest.score === 0) {
        total = 0;
        break;
      }
      total += wordBest.score;
      exact &&= wordBest.exact;
    }
    let score = total / qWords.length;
    const glued = qWords.join('');
    if (score === 0 && glued.length >= 2) {
      if (entry.joined.includes(glued)) {
        score = 0.65;
        exact = true;
      } else if (entry.initials.startsWith(glued)) {
        score = 0.5; // «ск» → «Сельдь копчёная»
        exact = true;
      }
    }
    if (score === 0) return;
    const match = { score: score * penalty, exact };
    if (!best || match.score > best.score) best = match;
  });
  return best;
}

/**
 * Filter + rank rows by a free-text query. Empty query keeps every row in its
 * original order. Otherwise the best matches come first (ties keep the input
 * order); `similarOnly` is true when no row matched literally.
 */
export function fuzzySearch<T>(
  rows: readonly T[],
  query: string,
  getIndex: (row: T) => SearchIndexEntry,
): { rows: T[]; similarOnly: boolean } {
  const variants = queryVariants(query);
  if (variants.length === 0) return { rows: [...rows], similarOnly: false };
  const hits: { row: T; match: SearchMatch; order: number }[] = [];
  rows.forEach((row, order) => {
    const match = matchEntry(getIndex(row), variants);
    if (match) hits.push({ row, match, order });
  });
  const anyExact = hits.some((h) => h.match.exact);
  // With literal hits present, keep only near-certain typos (1 slip in 5+ letters).
  const kept = anyExact ? hits.filter((h) => h.match.exact || h.match.score >= 0.5) : hits;
  kept.sort((a, b) => b.match.score - a.match.score || a.order - b.order);
  return { rows: kept.map((h) => h.row), similarOnly: kept.length > 0 && !anyExact };
}
